import { Select } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { OptionRender } from "./OptionRender"

import type { DescriptiveSelectProps, TypedOption } from "."

/**
 * Select with label + description per option.
 * @param {string} [props.translationKey] - keyPrefix for label/description lookups (use with `values: T[]`)
 * @param {T[] | TypedOption<T>[]} props.values - raw values (needs translationKey) or pre-built options
 * @param {string} [props.descriptionClassName] - class merged onto each option's description element
 * @param {...SelectProps<T, TypedOption<T>>} props - all other antd Select props
 */
export function DescriptiveSelect<T extends string | number>(
  props: DescriptiveSelectProps<T>
) {
  const { translationKey, values, descriptionClassName, ...rest } = props
  const { t } = useTranslation("translation", {
    keyPrefix: translationKey
  })
  const options = translationKey
    ? values.map(
        (value: T) =>
          ({
            label: t(value.toString()),
            value: value,
            description: t(`${value}_DESCRIPTION`)
          }) as TypedOption<typeof value>
      )
    : (values as TypedOption<T>[])
  const { value: selectedValue } = rest

  return (
    <Select<T, TypedOption<T>>
      options={options}
      optionRender={OptionRender(selectedValue, descriptionClassName)}
      {...rest}
    />
  )
}
